import { useEffect, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FeedbackView, type FeedbackContext } from '@metro/feedback'
import styles from './FeedbackVisualFixture.module.css'

/**
 * Private QA fixture for Playwright Feedback-form snapshots (web I port —
 * iOS Feedback module is the visual source of truth, no web Figma).
 * Mounts a single context + layout of {@link FeedbackView} on a blank
 * backdrop, so each screenshot is pixel-stable.
 *
 * URL contract:
 *   /__visual/feedback?context=<route|routeDetails|stationDetails>
 *                      &theme=<light|dark>
 *                      &layout=<overlay|sidePanel>
 *
 * `theme` defaults to `light`; `dark` forces `<html data-theme="dark">`.
 * Dev/QA-only route, excluded from indexing via `public/robots.txt`
 * `Disallow: /__visual/`. The form renders in its initial (empty) state —
 * the multi-select / ThankYou transitions are covered by the package unit
 * tests, not pixels.
 */
const VALID_CONTEXTS: ReadonlySet<FeedbackContext> = new Set<FeedbackContext>([
  'route',
  'routeDetails',
  'stationDetails',
])

function parseContext(raw: string | null): FeedbackContext {
  if (raw !== null && (VALID_CONTEXTS as Set<string>).has(raw)) return raw as FeedbackContext
  return 'route'
}

export function FeedbackVisualFixture(): ReactElement {
  const [params] = useSearchParams()
  const context = parseContext(params.get('context'))
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light'
  const layout = params.get('layout') === 'sidePanel' ? 'sidePanel' : 'overlay'

  useEffect(() => {
    const html = document.documentElement
    const prev = html.dataset.theme
    html.dataset.theme = theme
    html.classList.add('visual-fixture-active')
    return () => {
      if (prev === undefined) delete html.dataset.theme
      else html.dataset.theme = prev
      html.classList.remove('visual-fixture-active')
    }
  }, [theme])

  return (
    <div
      className={styles.frame}
      data-testid="feedback-visual-fixture"
      data-context={context}
      data-layout={layout}
    >
      <FeedbackView
        context={context}
        layout={layout}
        onClose={() => {}}
        key={`${context}-${layout}`}
      />
    </div>
  )
}

export default FeedbackVisualFixture
